import { forwardRef, useRef } from 'react';
import type { ComponentProps } from 'react';
import { Link } from 'react-router';
import { useQueryClient, type FetchQueryOptions } from '@tanstack/react-query';

// Omit React Router's built-in `prefetch` (type PrefetchBehavior) so we can
// reuse the name for our React Query prefetch options without a type clash.
type BaseLinkProps = Omit<ComponentProps<typeof Link>, 'prefetch'>;

interface PrefetchLinkProps extends BaseLinkProps {
  /**
   * Query-options object (same shape as queryClient.prefetchQuery input)
   * to prefetch on hover/focus/touchstart. Omit to use as a plain Link.
   */
  prefetch?: FetchQueryOptions;
}

/**
 * React Router `<Link>` with:
 *  1. `viewTransition` prop set by default — opts this nav into the
 *     global @view-transition CSS rule for a 220ms fade+lift.
 *  2. One-shot prefetch on hover / focus / touchstart when `prefetch`
 *     is provided. Uses the current React Query client.
 */
export const PrefetchLink = forwardRef<HTMLAnchorElement, PrefetchLinkProps>(
  function PrefetchLink(
    { prefetch, onMouseEnter, onFocus, onTouchStart, ...rest },
    ref,
  ) {
    const queryClient = useQueryClient();
    const fired = useRef(false);

    const trigger = () => {
      if (!prefetch || fired.current) return;
      fired.current = true;
      void queryClient.prefetchQuery(prefetch);
    };

    return (
      <Link
        ref={ref}
        viewTransition
        onMouseEnter={(e) => {
          trigger();
          onMouseEnter?.(e);
        }}
        onFocus={(e) => {
          trigger();
          onFocus?.(e);
        }}
        onTouchStart={(e) => {
          trigger();
          onTouchStart?.(e);
        }}
        {...rest}
      />
    );
  },
);
